/**
 * The caller's own bid capabilities (P4), from `GET /bid-capabilities`.
 *
 * Used to gate the admin route/nav and to hide money in the estimator. That hiding is COSMETIC —
 * `strip_financials` on the server is the actual boundary, and a blind role's responses simply do
 * not contain the numbers. This only stops the UI rendering empty columns and dead buttons.
 *
 * FAIL CLOSED while loading or on error: `null` means "not yet known", and every consumer treats
 * that as no-access. Defaulting to "allowed" would flash an admin screen at a standard user.
 *
 * Cached for the session like the catalog options — several components ask, and each must not
 * trigger its own request. It is an EXTERNAL STORE, not a plain cache: invalidating it must
 * re-notify already-mounted consumers (the nav bar is mounted for the app's lifetime), or an admin
 * who changes their own roles keeps the old gate and the old money visibility until a reload.
 */
import { useSyncExternalStore } from 'react';
import { isFeatureEnabled } from '../AppSettings/environmentConfig';
import { subscribeRuntimeConfig } from '../__settings__/runtimeConfig';
import HttpService from '../AppSettings/services/HttpService';
import { setFinancialVisible } from './financialDisplay';

const http = new HttpService();

export interface BidCapabilities {
  roles: string[];
  capabilities: string[];
  financialVisible: boolean;
  canMutateBids: boolean;
  isBidAdmin: boolean;
  canAssignRoles: boolean;
}

/** Everything denied — the shape used while loading and after a failure. */
export const NO_CAPABILITIES: BidCapabilities = {
  roles: [],
  capabilities: [],
  financialVisible: false,
  canMutateBids: false,
  isBidAdmin: false,
  canAssignRoles: false,
};

let cache: BidCapabilities | null = null;
let inflight: Promise<BidCapabilities> | null = null;
const listeners = new Set<() => void>();

/**
 * The value `isFeatureEnabled('bids')` had when the cache was last resolved.
 *
 * P1's runtime config arrives ASYNCHRONOUSLY (RuntimeConfigLoader renders nothing and fetches in
 * an effect), while NavBarAuth calls this hook on the very first render. So the flag read below
 * is normally the BAKED descriptor, not the tenant's. Without this, a tenant that turns `bids` ON
 * in config got the nav and routes — App re-renders when the config lands — but kept the denial
 * cached for the whole session: no capabilities, and `setFinancialVisible(false)` redacting money
 * on every pipeline surface, with the only invalidator sitting behind an `isBidAdmin` gate that
 * was itself false. The mirror case (baked ON, config OFF) cached an over-permissive `true`.
 */
let resolvedUnderBidsEnabled: boolean | null = null;

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function publish(next: BidCapabilities | null): void {
  cache = next;
  // keep the money formatter in lockstep: it is the thing that must never show a stale figure
  setFinancialVisible(Boolean(next?.financialVisible));
  listeners.forEach((l) => l());
}

function fromApi(data: any): BidCapabilities {
  return {
    roles: data?.roles ?? [],
    capabilities: data?.capabilities ?? [],
    financialVisible: Boolean(data?.financial_visible),
    canMutateBids: Boolean(data?.can_mutate_bids),
    isBidAdmin: Boolean(data?.is_bid_admin),
    canAssignRoles: Boolean(data?.can_assign_roles),
  };
}

export function loadBidCapabilities(): Promise<BidCapabilities> {
  // A client who did not opt into bids should make no bid requests at all. NavBarAuth calls this
  // unconditionally (hook order forbids conditionalising the call), so without this every
  // opted-out engineering client — and every legal client — fired one GET /bid-capabilities per
  // session for a feature they do not have.
  //
  // Denying rather than throwing keeps consumers on their existing "null/denied" path: this is a
  // shortcut for a request whose answer is already known, not a new failure mode.
  if (!isFeatureEnabled('bids')) {
    resolvedUnderBidsEnabled = false;
    publish(NO_CAPABILITIES);
    return Promise.resolve(NO_CAPABILITIES);
  }
  resolvedUnderBidsEnabled = true;
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;

  inflight = http
    .get<any>('/bid-capabilities')
    .then((res) => {
      publish(fromApi(res?.data));
      return cache as BidCapabilities;
    })
    // a failure must not open the gate: deny, and don't cache, so a transient error can recover
    .catch(() => {
      publish(null);              // deny, and tell every consumer
      return NO_CAPABILITIES;
    })
    .finally(() => {
      inflight = null;
    });

  return inflight;
}

/**
 * Drop the cache after a role change (or sign-out) and REFETCH.
 *
 * Clearing alone is not enough: mounted consumers (the nav bar lives for the app's lifetime) would
 * never re-read, so an admin who revokes their own role keeps the old tab, the old gate, and — via
 * `financialDisplay` — the old money visibility. That last one is the dangerous case: with the
 * stale flag still `true`, a now-blind caller's stripped line rates coerce to 0 and the detail page
 * renders a fabricated "$0". So this fails CLOSED immediately, then re-warms.
 */
export function invalidateBidCapabilities(): void {
  publish(null);          // fail closed + notify, synchronously
  inflight = null;
  void loadBidCapabilities();
}

/**
 * Re-resolve when the tenant's runtime config lands and it disagrees with what we resolved under.
 *
 * Subscribed at module load, once. Only fires when the flag actually FLIPS, so the ordinary case
 * (config confirms the baked value) costs nothing. Both directions matter: enabling recovers a
 * session that would otherwise be stuck denied with money redacted, and disabling closes one that
 * would otherwise keep an over-permissive `true`.
 */
subscribeRuntimeConfig(() => {
  if (resolvedUnderBidsEnabled === null) return;   // nothing resolved yet; the first load reads fresh config
  if (resolvedUnderBidsEnabled === isFeatureEnabled('bids')) return;
  invalidateBidCapabilities();                      // fails closed, notifies, then re-warms
});

function getSnapshot(): BidCapabilities | null {
  return cache;
}

/**
 * `null` until resolved. Consumers MUST treat null as denied rather than rendering optimistically.
 */
export function useBidCapabilities(): BidCapabilities | null {
  const capabilities = useSyncExternalStore(subscribe, getSnapshot, () => null);

  // kick off the first fetch; a no-op once cached or in flight
  if (capabilities === null && inflight === null) void loadBidCapabilities();

  return capabilities;
}
